import { createReducer } from '@reduxjs/toolkit'
import {
  Invoice,
  Resource,
  TableSorting,
  SortingBy,
  SortingOrder,
  LawsuitCircumstanceQuestion,
  NavigationItemStatus,
  BatchApiErrorElement,
  LawsuitDetails,
  LoadingStatus,
  LawsuitStatus,
  BankTransfer,
  LawsuitLinks,
  Message,
  CustomerService,
} from 'shared/types'
import {
  initResource,
  setFailed,
  setPending,
  setSucceeded,
  removeProperty,
} from 'shared/utils'
import {
  PartiesFormValues,
  LawsuitsListItem,
  CircumstancesFormValues,
  VerificationFormValues,
} from '../types'
import {
  getCircumstanceQuestions,
  clearUploadPowerOfAttorneyState,
  getPowerOfAttorneyTemplate,
  getRecommendedInvoices,
  uploadPowerOfAttorney,
  setSorting,
  fileLawsuit,
  setInvoiceFiles,
  setAdditionalFiles,
  setCircumstancesFormValues,
  resetLawsuitFillingProgress,
  setCircumstancesStatus,
  setDocumentsStatus,
  setPartiesFormValues,
  setPartiesStatus,
  setSummaryStatus,
  toggleDocumentConsent,
  getLawyerLawsuits,
  getLawsuits,
  getLawsuitDetails,
  downloadLawsuitFile,
  getLawsuitMessages,
  verifyLawsuitData,
  addDataCorrectionFile,
  removeDataCorrectionFile,
  setVerificationFormValues,
  setBankTransferInfo,
  resetLawsuitVerificationLoadingStatus,
  resetBankTransferInfoSettingLoadingStatus,
  correctLawsuitData,
  getBankTransfers,
  uploadBankTransferConfirmation,
  resetLawsuitDetails,
  getBankTransferFiles,
  verifyBankTransferFiles,
  getLawsuitHyperlinks,
  clearLawsuitProcessErrors,
  setFiledLawsuitFiles,
  updateActSignature,
  uploadFiledLawsuitFiles,
  updateLawsuit,
  setUpdateLawsuitFiles,
  sendMessage,
  setSendMessageFiles,
  clearSendMessageErrors,
  markLawsuitMessagesAsRead,
  withdrawLawsuit,
  clearLawsuitWithdrawState,
  sendLawsuitWithdrawalMessage,
  clearSendLawsuitWithdrawalMessageState,
  withdrawPowerOfAttorney,
  getPowerOfAttorneyPreview,
} from './actions'
import { GetLawyerLawsuitsResponse } from './actions.types'

interface State {
  powerOfAttorneyTemplate: Resource<Blob>
  uploadPowerOfAttorney: Resource
  fileLawsuit: Resource<undefined, BatchApiErrorElement[]>
  lawsuits: Resource<LawsuitsListItem[]>
  recommendedInvoices: Resource<{
    rows: Invoice[]
    totalCount: number
    areThereNoResults: boolean
  }>
  circumstances: {
    questions: Resource<LawsuitCircumstanceQuestion[]>
    formValues: CircumstancesFormValues
    status: NavigationItemStatus
  }
  documents: {
    invoiceFiles: File[]
    additionalFiles: File[]
    status: NavigationItemStatus
    isConsentAccepted: boolean
  }
  parties: {
    formValues: PartiesFormValues
    wasFormSaved: boolean
    status: NavigationItemStatus
  }
  summary: {
    status: NavigationItemStatus
  }
  sorting: TableSorting
  lawyerLawsuits: Resource<GetLawyerLawsuitsResponse>
  lawsuitDetails: Resource<LawsuitDetails>
  downloadLawsuitFile: Resource
  lawsuitMessages: Resource<Message[]>
  verifyLawsuitData: Resource
  verificationFormValues: VerificationFormValues
  dataCorrection: Resource<{
    files: Record<string | number, File>
  }>
  setBankTransferInfo: Resource
  bankTransfers: Resource<BankTransfer[]>
  uploadBankTransferConfirmation: Resource
  bankTransferFiles: Resource<{ id: number; name: string }[]>
  verifyBankTransferFiles: Resource
  hyperlinks: Resource<LawsuitLinks>
  filedLawsuitFiles: File[]
  updateActSignature: Resource
  uploadFiledLawsuitFiles: Resource
  updateLawsuitFiles: File[]
  updateLawsuit: Resource
  sendMessageFiles: File[]
  sendMessage: Resource
  markLawsuitMessagesAsRead: Resource
  withdrawLawsuit: Resource
  customerService: Resource<CustomerService>
  sendLawsuitWithdrawalMessage: Resource
  withdrawPowerOfAttorney: Resource
  powerOfAttorney: Resource
}

const initialState: State = {
  powerOfAttorneyTemplate: initResource(),
  uploadPowerOfAttorney: initResource(),
  lawsuits: initResource(),
  recommendedInvoices: initResource({
    rows: [],
    totalCount: 0,
    areThereNoResults: false,
  }),
  circumstances: {
    questions: initResource([]),
    formValues: {},
    status: NavigationItemStatus.ToDo,
  },
  documents: {
    invoiceFiles: [],
    additionalFiles: [],
    status: NavigationItemStatus.ToDo,
    isConsentAccepted: false,
  },
  parties: {
    formValues: {
      bankAccountNumber: '',
      contractorEmailAddress: '',
      contractorPhoneNumber: '',
      phoneNumber: '',
      emailAddress: '',
      contactCountryId: 0,
      contractorContactCountryId: 0,
    },
    wasFormSaved: false,
    status: NavigationItemStatus.ToDo,
  },
  summary: {
    status: NavigationItemStatus.ToDo,
  },
  sorting: {
    sortingBy: SortingBy.daysLeft,
    sortingOrder: SortingOrder.Asc,
  },
  fileLawsuit: initResource(),
  lawyerLawsuits: initResource({
    page: 1,
    pageSize: 10,
    results: [],
    totalRowsCount: 0,
  }),
  lawsuitDetails: initResource({
    id: 0,
    status: LawsuitStatus.NOT_VERIFIED,
    administrationFeePaymentStatus: null,
    actSignature: null,
    remarks: null,
    userRemarks: null,
    actionRequired: false,
    hasUnreadedMessages: false,
    hasAnyMessages: false,
    answers: [],
    files: [],
    statusTimings: [],
    invoice: {
      amount: 0,
      amountLeft: 0,
      contractorName: '',
      currency: '',
      daysLeft: 0,
      dueDate: '',
      id: 0,
      number: '',
    },
    client: {
      apartmentNumber: '',
      bankAccountNumber: '',
      buildingNumber: '',
      city: '',
      countryId: 0,
      name: '',
      street: '',
      contact: {
        countryId: 0,
        phoneNumber: '',
        emailAddress: '',
      },
      taxNumber: '',
      zipCode: '',
    },
    contractor: {
      apartmentNumber: '',
      bankAccountNumber: '',
      buildingNumber: '',
      city: '',
      countryId: 0,
      contact: {
        countryId: 0,
        phoneNumber: '',
        emailAddress: '',
      },
      name: '',
      street: '',
      taxNumber: '',
      zipCode: '',
    },
  }),
  downloadLawsuitFile: initResource(),
  lawsuitMessages: initResource([]),
  verifyLawsuitData: initResource(),
  verificationFormValues: {},
  dataCorrection: initResource({ files: {} }),
  setBankTransferInfo: initResource(),
  bankTransfers: initResource([]),
  uploadBankTransferConfirmation: initResource(),
  bankTransferFiles: initResource([]),
  verifyBankTransferFiles: initResource(),
  hyperlinks: initResource({
    europeanPaymentOrder: '',
    polishPaymentOrder: '',
  }),
  filedLawsuitFiles: [],
  updateActSignature: initResource(),
  uploadFiledLawsuitFiles: initResource(),
  updateLawsuitFiles: [],
  updateLawsuit: initResource(),
  sendMessageFiles: [],
  sendMessage: initResource(),
  markLawsuitMessagesAsRead: initResource(),
  withdrawLawsuit: initResource(),
  customerService: initResource({
    emailAddress: 'pomoc@cashcollector.eu',
    phoneNumber: '+ 48 798 437 769',
  }),
  sendLawsuitWithdrawalMessage: initResource(),
  withdrawPowerOfAttorney: initResource(),
  powerOfAttorney: initResource(),
}

export default createReducer(initialState, builder =>
  builder
    .addCase(getRecommendedInvoices.pending, state => {
      setPending(state.recommendedInvoices)
    })
    .addCase(
      getRecommendedInvoices.fulfilled,
      (state, { payload, meta: { arg } }) => {
        setSucceeded(state.recommendedInvoices, {
          ...payload,
          areThereNoResults: !(payload.totalCount || arg?.search),
        })
      }
    )
    .addCase(
      getRecommendedInvoices.rejected,
      (state, { error: { message } }) => {
        setFailed(state.recommendedInvoices, message)
      }
    )
    .addCase(getPowerOfAttorneyTemplate.pending, state => {
      setPending(state.powerOfAttorneyTemplate)
    })
    .addCase(getPowerOfAttorneyTemplate.fulfilled, (state, { payload }) => {
      setSucceeded(state.powerOfAttorneyTemplate, payload)
    })
    .addCase(
      getPowerOfAttorneyTemplate.rejected,
      (state, { error: { message } }) => {
        setFailed(state.powerOfAttorneyTemplate, message)
      }
    )
    .addCase(uploadPowerOfAttorney.pending, state => {
      setPending(state.uploadPowerOfAttorney)
    })
    .addCase(uploadPowerOfAttorney.fulfilled, (state, { payload }) => {
      setSucceeded(state.uploadPowerOfAttorney, payload)
    })
    .addCase(uploadPowerOfAttorney.rejected, (state, { payload }) => {
      setFailed(state.uploadPowerOfAttorney, payload?.errorCode)
    })
    .addCase(getCircumstanceQuestions.pending, state => {
      setPending(state.circumstances.questions)
    })
    .addCase(getCircumstanceQuestions.fulfilled, (state, { payload }) => {
      setSucceeded(state.circumstances.questions, payload)
    })
    .addCase(getCircumstanceQuestions.rejected, (state, { payload }) => {
      setFailed(state.circumstances.questions)
    })
    .addCase(fileLawsuit.pending, state => {
      setPending(state.fileLawsuit)
    })
    .addCase(fileLawsuit.fulfilled, state => {
      setSucceeded(state.fileLawsuit)
    })
    .addCase(fileLawsuit.rejected, (state, { payload }) => {
      setFailed(state.fileLawsuit, payload)
    })
    .addCase(setSorting, (state, { payload }) => {
      state.sorting = payload
    })
    .addCase(clearUploadPowerOfAttorneyState, state => {
      state.uploadPowerOfAttorney = initResource()
    })
    .addCase(setInvoiceFiles, (state, { payload }) => {
      state.documents.invoiceFiles = payload
    })
    .addCase(setAdditionalFiles, (state, { payload }) => {
      state.documents.additionalFiles = payload
    })
    .addCase(setDocumentsStatus, (state, { payload }) => {
      state.documents.status = payload
    })
    .addCase(toggleDocumentConsent, state => {
      state.documents.isConsentAccepted = !state.documents.isConsentAccepted
    })
    .addCase(setCircumstancesFormValues, (state, { payload }) => {
      state.circumstances.formValues = payload
    })
    .addCase(setCircumstancesStatus, (state, { payload }) => {
      state.circumstances.status = payload
    })
    .addCase(setPartiesFormValues, (state, { payload }) => {
      state.parties.formValues = payload
      state.parties.wasFormSaved = true
    })
    .addCase(setPartiesStatus, (state, { payload }) => {
      state.parties.status = payload
    })
    .addCase(setSummaryStatus, (state, { payload }) => {
      state.summary.status = payload
    })
    .addCase(resetLawsuitFillingProgress, state => {
      state.circumstances = initialState.circumstances
      state.documents = initialState.documents
      state.parties = initialState.parties
      state.fileLawsuit = initialState.fileLawsuit
      state.summary = {
        status: NavigationItemStatus.ToDo,
      }
    })
    .addCase(getLawsuits.pending, state => {
      setPending(state.lawsuits)
    })
    .addCase(getLawsuits.fulfilled, (state, { payload }) => {
      setSucceeded(state.lawsuits, payload)
    })
    .addCase(getLawsuits.rejected, (state, { error: { message } }) => {
      setFailed(state.lawsuits, message)
    })
    .addCase(getLawyerLawsuits.pending, (state, { meta: { arg } }) => {
      if (!arg.page || arg.page === 1) {
        state.lawyerLawsuits.data.page = 1
        state.lawyerLawsuits.data.results = []
      }
      setPending(state.lawyerLawsuits)
    })
    .addCase(getLawyerLawsuits.fulfilled, (state, { payload }) => {
      setSucceeded(state.lawyerLawsuits, {
        ...payload,
        results: [...state.lawyerLawsuits.data.results, ...payload.results],
      })
    })
    .addCase(getLawyerLawsuits.rejected, (state, { error: { message } }) => {
      setFailed(state.lawyerLawsuits, message)
    })
    .addCase(getLawsuitDetails.pending, state => {
      setPending(state.lawsuitDetails)
    })
    .addCase(getLawsuitDetails.fulfilled, (state, { payload }) => {
      setSucceeded(state.lawsuitDetails, payload)
    })
    .addCase(getLawsuitDetails.rejected, (state, { error: { message } }) => {
      setFailed(state.lawsuitDetails, message)
    })
    .addCase(downloadLawsuitFile.pending, state => {
      setPending(state.downloadLawsuitFile)
    })
    .addCase(downloadLawsuitFile.fulfilled, (state, { payload }) => {
      setSucceeded(state.downloadLawsuitFile, payload)
    })
    .addCase(downloadLawsuitFile.rejected, (state, { error: { message } }) => {
      setFailed(state.downloadLawsuitFile, message)
    })
    .addCase(getLawsuitMessages.pending, state => {
      setPending(state.lawsuitMessages)
    })
    .addCase(getLawsuitMessages.fulfilled, (state, { payload }) => {
      setSucceeded(state.lawsuitMessages, payload)
    })
    .addCase(getLawsuitMessages.rejected, (state, { error: { message } }) => {
      setFailed(state.lawsuitMessages, message)
    })
    .addCase(verifyLawsuitData.pending, state => {
      setPending(state.verifyLawsuitData)
    })
    .addCase(verifyLawsuitData.fulfilled, (state, { payload }) => {
      setSucceeded(state.verifyLawsuitData, payload)
    })
    .addCase(verifyLawsuitData.rejected, (state, { error: { message } }) => {
      setFailed(state.verifyLawsuitData, message)
    })
    .addCase(addDataCorrectionFile, (state, { payload: { file, fileId } }) => {
      state.dataCorrection.data.files = {
        ...state.dataCorrection.data.files,
        [fileId]: file,
      }
    })
    .addCase(removeDataCorrectionFile, (state, { payload }) => {
      state.dataCorrection.data.files = removeProperty(
        state.dataCorrection.data.files,
        payload
      )
    })
    .addCase(setVerificationFormValues, (state, { payload }) => {
      state.verificationFormValues = payload
    })
    .addCase(setBankTransferInfo.pending, state => {
      setPending(state.setBankTransferInfo)
    })
    .addCase(setBankTransferInfo.fulfilled, (state, { payload }) => {
      setSucceeded(state.setBankTransferInfo, payload)
    })
    .addCase(setBankTransferInfo.rejected, (state, { error: { message } }) => {
      setFailed(state.setBankTransferInfo, message)
    })
    .addCase(resetLawsuitVerificationLoadingStatus, state => {
      state.verifyLawsuitData.loading = LoadingStatus.Idle
    })
    .addCase(resetBankTransferInfoSettingLoadingStatus, state => {
      state.setBankTransferInfo.loading = LoadingStatus.Idle
    })
    .addCase(correctLawsuitData.pending, state => {
      setPending(state.dataCorrection)
    })
    .addCase(correctLawsuitData.fulfilled, state => {
      setSucceeded(state.dataCorrection)
    })
    .addCase(correctLawsuitData.rejected, (state, { error: { message } }) => {
      setFailed(state.dataCorrection, message)
    })
    .addCase(getBankTransfers.pending, state => {
      setPending(state.bankTransfers)
    })
    .addCase(getBankTransfers.fulfilled, (state, { payload }) => {
      setSucceeded(state.bankTransfers, payload)
    })
    .addCase(getBankTransfers.rejected, (state, { error: { message } }) => {
      setFailed(state.bankTransfers, message)
    })
    .addCase(uploadBankTransferConfirmation.pending, state => {
      setPending(state.uploadBankTransferConfirmation)
    })
    .addCase(uploadBankTransferConfirmation.fulfilled, state => {
      setSucceeded(state.uploadBankTransferConfirmation)
    })
    .addCase(
      uploadBankTransferConfirmation.rejected,
      (state, { error: { message } }) => {
        setFailed(state.uploadBankTransferConfirmation, message)
      }
    )
    .addCase(resetLawsuitDetails, state => {
      state.lawsuitDetails = initialState.lawsuitDetails
    })
    .addCase(getBankTransferFiles.pending, state => {
      setPending(state.bankTransferFiles)
    })
    .addCase(getBankTransferFiles.fulfilled, (state, { payload }) => {
      setSucceeded(state.bankTransferFiles, payload)
    })
    .addCase(getBankTransferFiles.rejected, (state, { error: { message } }) => {
      setFailed(state.uploadBankTransferConfirmation, message)
    })
    .addCase(verifyBankTransferFiles.pending, state => {
      setPending(state.bankTransferFiles)
    })
    .addCase(verifyBankTransferFiles.fulfilled, state => {
      setSucceeded(state.bankTransferFiles)
    })
    .addCase(
      verifyBankTransferFiles.rejected,
      (state, { error: { message } }) => {
        setFailed(state.uploadBankTransferConfirmation, message)
      }
    )
    .addCase(getLawsuitHyperlinks.pending, state => {
      setPending(state.hyperlinks)
    })
    .addCase(getLawsuitHyperlinks.fulfilled, (state, { payload }) => {
      setSucceeded(state.hyperlinks, payload)
    })
    .addCase(getLawsuitHyperlinks.rejected, (state, { error: { message } }) => {
      setFailed(state.hyperlinks, message)
    })
    .addCase(clearLawsuitProcessErrors, state => {
      state.verifyBankTransferFiles.loading = LoadingStatus.Idle
      state.verifyLawsuitData.loading = LoadingStatus.Idle
      state.dataCorrection.loading = LoadingStatus.Idle
      state.uploadBankTransferConfirmation.loading = LoadingStatus.Idle
      state.uploadPowerOfAttorney.loading = LoadingStatus.Idle
      state.setBankTransferInfo.loading = LoadingStatus.Idle
      state.lawsuitDetails.loading = LoadingStatus.Idle
    })
    .addCase(setFiledLawsuitFiles, (state, { payload }) => {
      state.filedLawsuitFiles = payload
    })
    .addCase(updateActSignature.pending, state => {
      setPending(state.updateActSignature)
    })
    .addCase(
      updateActSignature.fulfilled,
      (
        state,
        {
          meta: {
            arg: { actSignature },
          },
        }
      ) => {
        setSucceeded(state.updateActSignature)
        state.lawsuitDetails.data.actSignature = actSignature
      }
    )
    .addCase(updateActSignature.rejected, (state, { error: { message } }) => {
      setFailed(state.updateActSignature, message)
    })
    .addCase(uploadFiledLawsuitFiles.pending, state => {
      setPending(state.uploadFiledLawsuitFiles)
    })
    .addCase(uploadFiledLawsuitFiles.fulfilled, (state, { payload }) => {
      setSucceeded(state.uploadFiledLawsuitFiles)
    })
    .addCase(
      uploadFiledLawsuitFiles.rejected,
      (state, { error: { message } }) => {
        setFailed(state.uploadFiledLawsuitFiles, message)
      }
    )
    .addCase(updateLawsuit.pending, state => {
      setPending(state.updateLawsuit)
    })
    .addCase(updateLawsuit.fulfilled, (state, { payload }) => {
      setSucceeded(state.updateLawsuit)
    })
    .addCase(updateLawsuit.rejected, (state, { error: { message } }) => {
      setFailed(state.updateLawsuit, message)
    })
    .addCase(setUpdateLawsuitFiles, (state, { payload }) => {
      state.updateLawsuitFiles = payload
    })
    .addCase(sendMessage.pending, state => {
      setPending(state.sendMessage)
    })
    .addCase(sendMessage.fulfilled, (state, { payload }) => {
      setSucceeded(state.sendMessage)
      state.lawsuitDetails.data.actionRequired = false
    })
    .addCase(sendMessage.rejected, (state, { error: { message } }) => {
      setFailed(state.sendMessage, message)
    })
    .addCase(setSendMessageFiles, (state, { payload }) => {
      state.sendMessageFiles = payload
    })
    .addCase(clearSendMessageErrors, (state, { payload }) => {
      state.sendMessage.loading = LoadingStatus.Idle
      state.sendMessage.error = undefined
    })
    .addCase(markLawsuitMessagesAsRead.pending, state => {
      setPending(state.markLawsuitMessagesAsRead)
    })
    .addCase(markLawsuitMessagesAsRead.fulfilled, (state, { payload }) => {
      setSucceeded(state.markLawsuitMessagesAsRead)
      state.lawsuitDetails.data.hasUnreadedMessages = false
    })
    .addCase(
      markLawsuitMessagesAsRead.rejected,
      (state, { error: { message } }) => {
        setFailed(state.markLawsuitMessagesAsRead, message)
      }
    )
    .addCase(withdrawLawsuit.pending, state => {
      setPending(state.withdrawLawsuit)
    })
    .addCase(withdrawLawsuit.fulfilled, state => {
      setSucceeded(state.withdrawLawsuit)
    })
    .addCase(withdrawLawsuit.rejected, (state, { error: { message } }) => {
      setFailed(state.withdrawLawsuit, message)
    })
    .addCase(clearLawsuitWithdrawState, state => {
      state.withdrawLawsuit = initialState.withdrawLawsuit
    })
    .addCase(sendLawsuitWithdrawalMessage.pending, state => {
      setPending(state.sendLawsuitWithdrawalMessage)
    })
    .addCase(sendLawsuitWithdrawalMessage.fulfilled, state => {
      setSucceeded(state.sendLawsuitWithdrawalMessage)
      state.sendMessageFiles = []
    })
    .addCase(
      sendLawsuitWithdrawalMessage.rejected,
      (state, { error: { message } }) => {
        setFailed(state.sendLawsuitWithdrawalMessage, message)
      }
    )
    .addCase(clearSendLawsuitWithdrawalMessageState, (state, { payload }) => {
      state.sendLawsuitWithdrawalMessage =
        initialState.sendLawsuitWithdrawalMessage
    })
    .addCase(withdrawPowerOfAttorney.pending, state => {
      setPending(state.withdrawPowerOfAttorney)
      state.uploadPowerOfAttorney = initialState.uploadPowerOfAttorney
    })
    .addCase(withdrawPowerOfAttorney.fulfilled, state => {
      setSucceeded(state.withdrawPowerOfAttorney)
    })
    .addCase(
      withdrawPowerOfAttorney.rejected,
      (state, { error: { message } }) => {
        setFailed(state.withdrawPowerOfAttorney, message)
      }
    )
    .addCase(getPowerOfAttorneyPreview.pending, state => {
      setPending(state.powerOfAttorney)
    })
    .addCase(getPowerOfAttorneyPreview.fulfilled, state => {
      setSucceeded(state.powerOfAttorney)
    })
    .addCase(
      getPowerOfAttorneyPreview.rejected,
      (state, { error: { message } }) => {
        setFailed(state.powerOfAttorney, message)
      }
    )
)
